/** /people/identity-review - cross-account identity unification
 *  queue. The identity-unifier worker auto-merges high-confidence
 *  pairs (>=0.95) and proposes the 0.7-0.95 band here as side-by-side
 *  cards with the shared signals highlighted. One tap confirms (the
 *  two person nodes merge) or rejects. Mirrors the contact-aggregation
 *  spec: a Google-Photos-style queue the user drains at their pace. */
import { TeachingBanner } from "../components/TeachingBanner";
import { useCallback, useEffect, useState } from "react";
import { Check, GitMerge, Users, X } from "lucide-react";
import { api, type IdentityMergeCandidate } from "../lib/api";

const SIGNAL_LABEL: Record<string, string> = {
  linkedin_member_id: "Same LinkedIn member id",
  email_overlap: "Shared email address",
  phone_overlap: "Shared phone number",
  name_plus_shared_address: "Same name + shared address",
  shared_org_first_name: "Same org + first name",
};

export function IdentityReview() {
  const [items, setItems] = useState<IdentityMergeCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .identityMergeCandidates("pending")
      .then((d) => {
        setItems(d.candidates);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2500);
  };

  const act = async (
    id: string,
    kind: "accept" | "reject",
  ): Promise<void> => {
    setBusyId(id);
    try {
      if (kind === "accept") {
        await api.identityMergeAccept(id);
        flash("Merged into one person");
      } else {
        await api.identityMergeReject(id);
        flash("Kept separate");
      }
      setItems((cur) => (cur ? cur.filter((c) => c.id !== id) : cur));
    } catch (e) {
      flash((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <TeachingBanner id="identity_review_overview" />
      <div className="mb-6 flex items-center gap-3">
        <GitMerge className="h-6 w-6 text-indigo-500" />
        <div>
          <h1 className="text-xl font-semibold">Identity review</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Same person across Gmail, LinkedIn, WhatsApp, and contacts.
            Confirm to weave them into one node.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400">
          <Users className="mx-auto mb-2 h-8 w-8 opacity-60" />
          Nothing to review. Memso has not found ambiguous duplicates.
        </div>
      )}

      <div className="space-y-4">
        {(items ?? []).map((c) => (
          <div
            key={c.id}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Likely the same person
              </span>
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                {(c.merge_score * 100).toFixed(0)}% match
              </span>
            </div>

            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
              <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/40">
                <div className="font-medium">
                  {c.person_a?.label ?? "Unknown"}
                </div>
                <div className="mt-1 truncate text-xs text-slate-400">
                  {c.person_a?.id}
                </div>
              </div>
              <GitMerge className="h-5 w-5 text-slate-400" />
              <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/40">
                <div className="font-medium">
                  {c.person_b?.label ?? "Unknown"}
                </div>
                <div className="mt-1 truncate text-xs text-slate-400">
                  {c.person_b?.id}
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(c.similarity).map(([sig, w]) => (
                <span
                  key={sig}
                  className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                >
                  {SIGNAL_LABEL[sig] ?? sig} ({w})
                </span>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <button
                disabled={busyId === c.id}
                onClick={() => act(c.id, "accept")}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" /> Confirm same person
              </button>
              <button
                disabled={busyId === c.id}
                onClick={() => act(c.id, "reject")}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <X className="h-4 w-4" /> Keep separate
              </button>
            </div>
          </div>
        ))}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-md bg-slate-900 px-4 py-2 text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900">
          {toast}
        </div>
      )}
    </div>
  );
}
